import { useEffect } from "react";

const isScrollable = (node) => {
  if (!node || node.scrollHeight <= node.clientHeight + 1) return false;
  const overflowY = window.getComputedStyle(node).overflowY;
  return overflowY === "auto" || overflowY === "scroll";
};

// The scroll element is the explicit target when given, otherwise the nearest
// scrollable ancestor of the container (falling back to the page).
const resolveScrollTarget = (container, scrollTargetRef) => {
  if (scrollTargetRef?.current) return scrollTargetRef.current;
  let node = container;
  while (node && node !== document.body && node !== document.documentElement) {
    if (isScrollable(node)) return node;
    node = node.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

/**
 * Forward wheel events on `containerRef` to a scroll element so the whole area
 * scrolls the document — even over widgets that swallow wheel events (e.g. a
 * product/service table's internal scroller). Runs in the CAPTURE phase so it
 * sees the event before a child can stopPropagation, then scrolls the target
 * itself. A genuinely scrollable inner element (other than the scroll target)
 * that isn't yet at its edge is left to scroll natively.
 *
 * @param {React.RefObject<HTMLElement>} containerRef   element to listen on
 * @param {React.RefObject<HTMLElement>} [scrollTargetRef] explicit scroll element;
 *        when omitted, the nearest scrollable ancestor of the container is used
 * @param {boolean} [enabled=true] when false, no listener is attached
 */
const useScrollForwardOnWheel = (containerRef, scrollTargetRef, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const container = containerRef?.current;
    if (!container) return undefined;

    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      // Leave native horizontal scrolling alone (trackpad pan, shift+wheel).
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const scrollEl = resolveScrollTarget(container, scrollTargetRef);
      if (!scrollEl) return;

      // Walk from the target up to the container; if a scrollable element other
      // than the scroll target can still absorb this delta, let it handle it.
      let node = e.target;
      while (node && node !== container) {
        if (node !== scrollEl && isScrollable(node)) {
          const atTop = node.scrollTop <= 0;
          const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
          if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) return;
        }
        node = node.parentElement;
      }

      if (scrollEl.scrollHeight <= scrollEl.clientHeight) return; // nothing to scroll
      scrollEl.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      e.preventDefault();
    };

    container.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => container.removeEventListener("wheel", onWheel, { capture: true });
  }, [containerRef, scrollTargetRef, enabled]);
};

export default useScrollForwardOnWheel;
